"use client";

import { useState } from "react";
import Link from "next/link";
import { getSiteContent } from "@/content";
import { type Locale } from "@/lib/site";

export function CareersPage({ locale }: { locale: Locale }) {
  const t = getSiteContent(locale).pages.careers;
  const [copied, setCopied] = useState(false);

  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(t.application.email);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = t.application.email;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  return (
    <main className="w-full overflow-x-hidden">
      <section className="relative overflow-hidden bg-glacier-white px-margin-mobile pb-section-gap pt-[156px] md:px-margin-desktop md:pt-[168px]">
        <div className="mx-auto grid max-w-container-max grid-cols-1 items-center gap-gutter lg:grid-cols-2">
          <div className="space-y-stack-md text-center lg:text-left">
            <div className="inline-flex items-center gap-2 rounded-full border border-border-pale bg-white/70 px-5 py-2 font-label-md text-secondary shadow-sm backdrop-blur-md">
              <span className="material-symbols-outlined text-[18px]">public</span>
              {t.hero.badge}
            </div>
            <h1 className="font-display-lg text-display-lg-mobile text-primary md:text-display-lg">{t.hero.title}</h1>
            <p className="mx-auto max-w-2xl font-body-lg text-body-lg text-on-surface-variant lg:mx-0">{t.hero.copy}</p>
            <div className="flex flex-col justify-center gap-4 pt-stack-sm sm:flex-row lg:justify-start">
              <a className="rounded-xl bg-parcel-orange px-8 py-4 font-label-md text-white shadow-lg transition-all hover:brightness-110 active:scale-95" href="#job-list">
                {t.hero.primaryCta}
              </a>
              <a className="rounded-xl border-2 border-primary-container bg-white px-8 py-4 font-label-md text-primary-container transition-all hover:bg-surface-container-low" href="#application">
                {t.hero.secondaryCta}
              </a>
            </div>
          </div>
          <div className="ui-panel relative min-h-[360px] overflow-hidden bg-primary-container shadow-2xl">
            <img className="absolute inset-0 h-full w-full object-cover opacity-80" src="/assets/images/cargo-plane.png" alt={t.hero.imageAlt} width={1200} height={760} />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/35 via-transparent to-primary/80" />
          </div>
        </div>
      </section>

      <section className="bg-white px-margin-mobile py-section-gap md:px-margin-desktop">
        <div className="mx-auto max-w-container-max">
          <div className="mb-stack-lg">
            <h2 className="font-headline-lg text-headline-lg text-primary">{t.missionTitle}</h2>
            <div className="mt-2 h-1 w-20 rounded-full bg-parcel-orange" />
          </div>
          <div className="grid grid-cols-1 gap-gutter md:grid-cols-5">
            {t.missions.map(([icon, title, copy]) => (
              <article className="ui-card group flex flex-col gap-4 border border-border-pale bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-lg" key={title}>
                <div className="ui-icon-tile ui-icon-tile-md bg-primary-container/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                  <span className="material-symbols-outlined">{icon}</span>
                </div>
                <h3 className="font-headline-md text-primary">{title}</h3>
                <p className="font-body-sm text-on-surface-variant">{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-surface-container-low px-margin-mobile py-section-gap md:px-margin-desktop">
        <div className="mx-auto max-w-container-max">
          <div className="mb-section-gap text-center">
            <h2 className="font-headline-lg text-headline-lg text-primary">{t.valuesTitle}</h2>
            <p className="mt-4 font-body-md text-on-surface-variant">{t.valuesCopy}</p>
          </div>
          <div className="grid grid-cols-1 gap-gutter md:grid-cols-4">
            {t.values.map(([icon, title, copy]) => (
              <article className="flex flex-col items-center p-stack-lg text-center" key={title}>
                <span className="material-symbols-outlined mb-4 text-[48px] text-secondary" style={{ fontVariationSettings: "'FILL' 1" }}>
                  {icon}
                </span>
                <h3 className="font-headline-md text-primary">{title}</h3>
                <p className="mt-2 font-body-sm text-on-surface-variant">{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-margin-mobile py-section-gap md:px-margin-desktop">
        <div className="mx-auto max-w-container-max">
          <h2 className="mb-stack-lg text-center font-headline-lg text-headline-lg text-primary">{t.stageTitle}</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-6 md:grid-rows-2">
            {t.stages.map((stage, index) => {
              const [iconOrKicker, title, copy] = stage;
              if (index === 0) {
                return (
                  <article className="ui-card relative min-h-[360px] overflow-hidden border border-border-pale bg-white p-stack-lg md:col-span-2 md:row-span-2" key={title}>
                    <img className="absolute inset-0 h-full w-full object-cover opacity-15" src="/assets/images/warehouse-hub.png" alt="" width={1200} height={760} />
                    <div className="relative z-10 flex h-full flex-col justify-end">
                      <span className="font-label-sm text-parcel-orange">{iconOrKicker}</span>
                      <h3 className="font-headline-md text-primary">{title}</h3>
                      <p className="mt-2 font-body-sm text-on-surface-variant">{copy}</p>
                    </div>
                  </article>
                );
              }

              return (
                <article className={`ui-card flex gap-4 border p-stack-lg transition-colors hover:border-secondary md:col-span-2 ${index === t.stages.length - 1 ? "border-primary bg-primary text-white" : "border-border-pale bg-white"}`} key={title}>
                  <div className={`ui-icon-round ui-icon-tile-md shrink-0 ${index === t.stages.length - 1 ? "bg-white/10 text-white" : "bg-secondary/10 text-secondary"}`}>
                    <span className="material-symbols-outlined">{iconOrKicker}</span>
                  </div>
                  <div>
                    <h3 className={`font-headline-md ${index === t.stages.length - 1 ? "text-white" : "text-primary"}`}>{title}</h3>
                    <p className={`mt-1 font-body-sm ${index === t.stages.length - 1 ? "text-white/80" : "text-on-surface-variant"}`}>{copy}</p>
                    {index === t.stages.length - 1 ? (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {["React", "Node.js", "Figma"].map((tag) => (
                          <span className="rounded bg-white/10 px-2 py-1 text-[10px] uppercase" key={tag}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-glacier-white px-margin-mobile py-section-gap md:px-margin-desktop" id="job-list">
        <div className="mx-auto max-w-container-max">
          <div className="mb-stack-lg flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <h2 className="font-headline-lg text-headline-lg text-primary">{t.jobsTitle}</h2>
              <p className="mt-2 font-body-md text-on-surface-variant">{t.jobsCopy}</p>
            </div>
            <span className="font-label-sm text-on-surface-variant">{t.jobsUpdated}</span>
          </div>
          <div className="overflow-x-auto rounded-card border border-border-pale bg-white">
            <table className="w-full min-w-[760px] text-left">
              <thead className="bg-surface-container text-primary">
                <tr>
                  {t.jobHeaders.map((header, index) => (
                    <th className={`px-6 py-4 font-label-md text-label-md ${index === t.jobHeaders.length - 1 ? "text-right" : ""}`} key={header}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border-pale font-body-sm text-on-surface">
                {t.jobs.map(([title, department, place, type]) => (
                  <tr className="transition-colors hover:bg-surface-container-low" key={title}>
                    <td className="px-6 py-5 font-bold text-primary">{title}</td>
                    <td className="px-6 py-5">{department}</td>
                    <td className="px-6 py-5">{place}</td>
                    <td className="px-6 py-5">{type}</td>
                    <td className="px-6 py-5 text-right">
                      <a className="font-label-md text-secondary hover:underline" href="#application">
                        {t.applyLabel}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-white px-margin-mobile py-section-gap md:px-margin-desktop" id="application">
        <div className="mx-auto grid max-w-container-max grid-cols-1 items-center gap-section-gap lg:grid-cols-2">
          <div>
            <h2 className="font-headline-lg text-headline-lg text-primary">{t.application.title}</h2>
            <p className="mt-stack-md font-body-lg text-body-lg text-on-surface-variant">{t.application.copy}</p>
            <ul className="mt-stack-lg space-y-6">
              {[
                ["alternate_email", t.application.emailLabel, t.application.email, "text-secondary font-bold"],
                ["subject", t.application.subjectLabel, t.application.subject, "text-on-surface-variant"],
                ["attach_file", t.application.attachmentLabel, t.application.attachment, "text-on-surface-variant"]
              ].map(([icon, label, value, valueClass]) => (
                <li className="flex gap-4" key={label}>
                  <span className="ui-icon-round ui-icon-tile-md shrink-0 bg-secondary text-white">
                    <span className="material-symbols-outlined text-[20px]">{icon}</span>
                  </span>
                  <div>
                    <h3 className="font-label-md text-primary">{label}</h3>
                    <p className={`mt-1 font-body-sm ${valueClass}`}>{value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <aside className="ui-card border-2 border-dashed border-secondary/30 bg-secondary/5 p-10 text-center">
            <span className="material-symbols-outlined text-[64px] text-secondary">rocket_launch</span>
            <h3 className="mt-6 font-headline-md text-primary">{t.application.cardTitle}</h3>
            <p className="mt-2 font-body-sm text-on-surface-variant">{t.application.cardCopy}</p>
            <button className={`mt-8 w-full rounded-xl px-6 py-4 font-label-md text-white transition-all ${copied ? "bg-success" : "bg-primary hover:bg-secondary"}`} type="button" onClick={copyEmail}>
              {copied ? t.application.copiedButton : t.application.copyButton}
            </button>
            <p className="mt-4 font-label-sm text-on-surface-variant/70">{t.application.privacy}</p>
          </aside>
        </div>
      </section>

      <section className="ambient-deep-bg relative overflow-hidden bg-primary px-margin-mobile py-section-gap text-center text-white md:px-margin-desktop">
        <div className="relative z-10 mx-auto max-w-container-max">
          <h2 className="font-display-lg text-display-lg-mobile md:text-display-lg">{t.cta.title}</h2>
          <p className="mx-auto mt-stack-lg max-w-xl font-body-lg text-white/70">{t.cta.copy}</p>
          <Link className="mt-stack-lg inline-flex items-center gap-3 rounded-xl bg-parcel-orange px-10 py-5 font-label-md text-white shadow-2xl transition-transform hover:scale-105" href={`mailto:${t.application.email}`}>
            <span className="material-symbols-outlined">send</span>
            {t.cta.action}
          </Link>
        </div>
      </section>
    </main>
  );
}
